<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  ArrowPathIcon,
  CircleStackIcon,
  GlobeAltIcon,
  UserCircleIcon,
} from '@heroicons/vue/24/outline';
import CopyButton from '@/components/aml-review/CopyButton.vue';
import EntityId from '@/components/ui/EntityId.vue';
import TimestampWithIso from '@/components/ui/TimestampWithIso.vue';
import { dashedLabel } from '@/components/cards/cardDisplay';
import { useImmersveApiClient } from '@/hooks/useImmersveApiClient';
import { AccountActions } from '@/integrations/Immersve/auth/actions';
import type { FundingSource } from '@/integrations/Immersve/funding-sources/types';
import { useAuthStore } from '@/stores/auth.store';
import { balanceLabel, isInMinorUnits } from './fundingSourceDisplay';

const props = defineProps<{ fundingSourceId: string }>();

const api = useImmersveApiClient();
const authStore = useAuthStore();
const fundingSource = ref<FundingSource | null>(null);
const loading = ref(true);
const error = ref<Error | null>(null);

async function load() {
  loading.value = true;
  error.value = null;
  fundingSource.value = null;
  try {
    fundingSource.value = await api.getFundingSource(props.fundingSourceId);
  } catch (err) {
    error.value = err instanceof Error ? err : new Error(String(err));
  } finally {
    loading.value = false;
  }
}

watch(() => props.fundingSourceId, load, { immediate: true });

// Listing the account's funding sources takes account:read, which
// funding-source:read alone doesn't give.
const canReadAccount = computed(
  () =>
    !!fundingSource.value &&
    (authStore.user?.isAuthorised(
      AccountActions.Read,
      `account:${fundingSource.value.accountId}`
    ) ??
      false)
);
</script>

<template>
  <div>
    <div
      v-if="loading"
      data-test="funding-source-loading"
      class="flex items-center gap-2 py-6 text-sm text-gray-500"
    >
      <ArrowPathIcon class="h-4 w-4 animate-spin" />
      Loading…
    </div>
    <p v-else-if="error" data-test="funding-source-error" class="py-6 text-sm text-red-600">
      {{ error.message }}
    </p>
    <!-- Laid out as the payment view is: the figure people come for, with who
    it belongs to, in a summary beside the details. -->
    <template v-else-if="fundingSource">
      <div class="flex min-w-0 items-center gap-x-6 border-b border-gray-200 pb-8">
        <div class="flex size-16 flex-none items-center justify-center rounded-full bg-gray-100">
          <CircleStackIcon class="size-8 text-gray-500" aria-hidden="true" />
        </div>
        <div class="min-w-0">
          <div class="flex min-w-0 items-center gap-x-1.5 text-sm/6 text-gray-500">
            <span class="shrink-0 whitespace-nowrap">Funding source</span>
            <EntityId :value="fundingSource.id" label="funding source ID" />
          </div>
          <h2
            class="mt-1 text-base font-semibold text-gray-900 first-letter:uppercase"
            data-test="funding-source-purpose"
          >
            {{ dashedLabel(fundingSource.purpose) }}
          </h2>
        </div>
      </div>

      <div class="mt-8 grid grid-cols-1 items-start gap-x-8 gap-y-8 lg:grid-cols-3">
        <div class="lg:col-start-3 lg:row-end-1">
          <h2 class="sr-only">Summary</h2>
          <div class="rounded-lg bg-gray-50 outline-1 -outline-offset-1 outline-gray-900/5">
            <dl class="pb-6">
              <div class="px-6 pt-6">
                <dt class="text-sm/6 font-semibold text-gray-900">Balance</dt>
                <dd
                  class="mt-1 text-2xl font-semibold break-all text-gray-900 tabular-nums"
                  data-test="funding-source-balance"
                >
                  <template v-if="isInMinorUnits(fundingSource)">
                    {{ fundingSource.balance }} {{ fundingSource.balanceCurrency }}
                    <span class="block text-xs font-normal text-gray-500">in minor units</span>
                  </template>
                  <template v-else>{{ balanceLabel(fundingSource) }}</template>
                </dd>
              </div>
              <div class="mt-6 flex w-full gap-x-4 border-t border-gray-200 px-6 pt-6">
                <dt class="flex-none">
                  <span class="sr-only">Account</span>
                  <UserCircleIcon class="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd class="flex min-w-0 flex-col items-start text-sm/6 text-gray-500">
                  Account
                  <EntityId
                    v-if="canReadAccount"
                    :value="fundingSource.accountId"
                    label="account ID"
                    :to="`/funding-sources?account=${encodeURIComponent(fundingSource.accountId)}`"
                    data-test="funding-source-account-link"
                  />
                  <EntityId v-else :value="fundingSource.accountId" label="account ID" />
                </dd>
              </div>
              <div class="mt-4 flex w-full gap-x-4 px-6">
                <dt class="flex-none">
                  <span class="sr-only">Network</span>
                  <GlobeAltIcon class="h-6 w-5 text-gray-400" aria-hidden="true" />
                </dt>
                <dd class="flex min-w-0 flex-col items-start text-sm/6 text-gray-500">
                  Network
                  <span class="text-gray-900" data-test="funding-source-network">
                    {{ fundingSource.network || '–' }}
                  </span>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        <div
          class="-mx-4 px-4 py-8 shadow-xs ring-1 ring-gray-200 sm:mx-0 sm:rounded-lg sm:px-8 lg:col-span-2 lg:row-span-2 lg:row-end-2 xl:px-12"
        >
          <h2 class="text-base font-semibold text-gray-900">Details</h2>
          <dl class="mt-6 grid grid-cols-1 gap-y-6 text-sm/6 sm:grid-cols-2">
            <div class="min-w-0 sm:pr-4">
              <dt class="text-gray-500">Created</dt>
              <dd class="mt-0.5"><TimestampWithIso :value="fundingSource.createdAt" /></dd>
            </div>
            <div class="min-w-0 sm:pl-4">
              <dt class="text-gray-500">Timeout block</dt>
              <dd class="mt-0.5 text-gray-900 tabular-nums">
                {{ fundingSource.timeoutBlockNumber ?? '–' }}
              </dd>
            </div>
            <div class="min-w-0 border-t border-gray-200 pt-6 sm:col-span-2">
              <dt class="text-gray-500">External ID</dt>
              <dd class="mt-0.5 flex min-w-0 items-center gap-x-1.5 text-gray-900">
                <span
                  class="break-all"
                  :class="{ 'font-mono': fundingSource.externalId }"
                  data-test="funding-source-external-id"
                >
                  {{ fundingSource.externalId || '–' }}
                </span>
                <CopyButton :value="fundingSource.externalId" label="external ID" />
              </dd>
            </div>
            <div class="min-w-0 sm:col-span-2">
              <dt class="text-gray-500">Funding channel</dt>
              <dd class="mt-0.5 flex min-w-0 items-center gap-x-1.5 text-gray-900">
                <span class="break-all" :class="{ 'font-mono': fundingSource.fundingChannelId }">
                  {{ fundingSource.fundingChannelId || '–' }}
                </span>
                <CopyButton :value="fundingSource.fundingChannelId" label="funding channel ID" />
              </dd>
            </div>
            <div class="min-w-0 sm:col-span-2">
              <dt class="text-gray-500">Type</dt>
              <dd
                class="mt-0.5 break-all text-gray-900"
                :class="{ 'font-mono': fundingSource.fundingSourceType }"
              >
                {{ fundingSource.fundingSourceType || '–' }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </template>
  </div>
</template>
